// Account-link payload decoding ("SH1").
//
// Port of sh-overwolf/src/core/utils/account-link.ts (the ENCODER, which is the
// authority). The Overwolf app opens the account pages (/ow-plan, and later
// /ow-cancel) with a `d` query param carrying the signed-in user's e-mail,
// password and current plan, so the page never has to ask them to log in again.
//
// WHAT THIS IS AND IS NOT: SH1 is obfuscation, not protection. The encoder ships
// in the Overwolf client bundle and the scheme is documented on both sides (it
// has to be - this file is the other side), so anyone holding a link can recover
// the password from it, and that password is not revocable the way a token would
// be. The trade-off was raised and accepted deliberately; it is recorded in
// sh-overwolf/docs/superpowers/specs/2026-08-21-no-plan-tier-and-account-links-design.md
// along with the token-based alternatives that were offered instead.
//
// The discipline that goes with that decision, and that callers here must keep:
// LOG THAT A LINK WAS OPENED, NEVER THE URL AND NEVER A DECODED FIELD. Songhunt's
// diagnostic logs are downloaded by users and pasted into support tickets.
//
// ============================ THE SH1 SCHEME ============================
//
// Encode:
//   1. Join with U+001F:  SH1 <US> email <US> password <US> plan <US> issuedAtMs
//      (U+001F cannot occur in an e-mail, a password or a plan name, so no field
//      ever needs escaping.)
//   2. UTF-8 encode.
//   3. XOR every byte with the repeating XOR_KEY.
//   4. Reverse the bytes.
//   5. Standard base64, '=' padding stripped.
//   6. Transliterate the standard base64 alphabet to SH_ALPHABET, position for
//      position - 64 URL-safe characters, so no percent-encoding ever happens.
//
// Decode runs those six steps backwards. Because of the reversal in step 4 the
// SH1 marker ends up at the TAIL of the blob, so a blob truncated at the FRONT
// still carries a valid marker with a mangled issuedAtMs - which is why the
// freshness check below is required, not advisory.
// =======================================================================

/** Payload marker + version. Field 0 of every payload. */
export const ACCOUNT_LINK_VERSION = 'SH1';

/** Field delimiter: ASCII UNIT SEPARATOR (U+001F). */
export const FIELD_SEPARATOR = '\u001F';

/** Repeating XOR key (step 3). Must match the encoder byte for byte. */
export const XOR_KEY = 'h#7Kq2$Lz9';

/** The standard base64 alphabet (step 5's output). */
export const STD_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

/** The substitution alphabet (step 6). Same 64 slots, URL-safe characters. */
export const SH_ALPHABET = 'kgRVPacDA5SBuziUnf8M97Ex4p0r3TmWsohO-jdeF2QKJL1IZvHY_NbGlwtXy6qC';

/** How long a link stays usable. Confirmed with the product owner 2026-08-21. */
export const ACCOUNT_LINK_MAX_AGE_MS = 60 * 60 * 1000; // 1 hour

/** The plan the user is on, as sent by the Overwolf app. */
export type AccountPlan = 'none' | 'hunter' | 'streamer';

export interface AccountPayload {
  email: string;
  password: string;
  plan: AccountPlan;
  /** Milliseconds since the epoch, at the moment the link was built. */
  issuedAtMs: number;
}

const PLANS: readonly string[] = ['none', 'hunter', 'streamer'];

function xorBytes(bytes: Uint8Array): Uint8Array {
  const key = new TextEncoder().encode(XOR_KEY);
  const out = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) {
    out[i] = bytes[i] ^ key[i % key.length];
  }
  return out;
}

function transliterate(text: string, from: string, to: string): string {
  let out = '';
  for (const char of text) {
    const index = from.indexOf(char);
    // A character outside the source alphabet means the blob is not ours; pass
    // it through so decoding fails on the version check rather than here.
    out += index === -1 ? char : to[index];
  }
  return out;
}

/**
 * Decode a `d` blob. Returns null for anything that is not a well-formed SH1
 * payload - missing, truncated, tampered with, or built by something else.
 *
 * Freshness is NOT checked here: `isFresh` is separate so a caller can decode
 * and report "expired" distinctly from "malformed" if it ever needs to.
 */
export function decodeAccountPayload(blob: string): AccountPayload | null {
  try {
    if (!blob) return null;

    const base64 = transliterate(blob, SH_ALPHABET, STD_ALPHABET);
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);

    const binary = atob(padded);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);

    const joined = new TextDecoder().decode(xorBytes(bytes.reverse()));
    const fields = joined.split(FIELD_SEPARATOR);
    if (fields.length !== 5) return null;

    const [version, email, password, plan, issuedAtMs] = fields;
    if (version !== ACCOUNT_LINK_VERSION) return null;
    if (!email || !password) return null;
    if (!PLANS.includes(plan)) return null;

    const issuedAt = Number(issuedAtMs);
    if (!Number.isFinite(issuedAt) || issuedAt <= 0) return null;

    return { email, password, plan: plan as AccountPlan, issuedAtMs: issuedAt };
  } catch {
    return null;
  }
}

/**
 * True while the link is still usable. A link from the future is rejected too:
 * a clock skew big enough to matter means the timestamp cannot be trusted as an
 * expiry at all, and accepting it would hand out an unbounded window.
 */
export function isFresh(payload: AccountPayload, now: number = Date.now()): boolean {
  const age = now - payload.issuedAtMs;
  return age >= -ACCOUNT_LINK_MAX_AGE_MS && age <= ACCOUNT_LINK_MAX_AGE_MS;
}
